import { Component, inject, OnDestroy, OnInit, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyView } from '@api-proxies';
import { CompanyService } from '@http/clients/company.service';
import { EntityTypes } from '@models/enums/EntityTypes';
import { TabNames } from '@models/enums/TabNames';
import { AwaiterService } from '@services/awaiter.service';
import { TabCounterService, TabCounterType } from '@services/tab-counter.service';
import { ActivityLogGridComponent } from '@shared/activity-log-grid/activity-log-grid.component';
import { KebabMenuComponent } from '@shared/kebab-menu/kebab-menu.component';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { NotesComponent } from '@shared/notes/notes.component';
import { SectionHeaderComponent } from '@shared/section-header/section-header.component';
import { getTab, processTabParameter } from '@utils/tab-navigation';
import {
    CounterBadgeComponent,
    ModalService,
    StatusIndicatorComponent,
    TabItemComponent,
    TabViewComponent
} from 'ngx-lib';
import { Subscription } from 'rxjs';
import { CompaniesSingleBuildingsComponent } from './companies-single-buildings/companies-single-buildings.component';
import { CompaniesSingleContactsComponent } from './companies-single-contacts/companies-single-contacts.component';
import { CompaniesSingleOpportunitiesComponent } from './companies-single-opportunities/companies-single-opportunities.component';
import { CompaniesSingleOverviewComponent } from './companies-single-overview/companies-single-overview.component';

@Component({
    selector: 'app-companies-single',
    templateUrl: './companies-single.component.html',
    styleUrls: ['./companies-single.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SectionHeaderComponent,
        StatusIndicatorComponent,
        TabViewComponent,
        TabItemComponent,
        CounterBadgeComponent,
        KebabMenuComponent,
        CompaniesSingleOverviewComponent,
        CompaniesSingleBuildingsComponent,
        CompaniesSingleContactsComponent,
        CompaniesSingleOpportunitiesComponent,
        NotesComponent,
        ActivityLogGridComponent
    ]
})
export class CompaniesSingleComponent implements OnInit, OnDestroy {
    @ViewChild(TabViewComponent)
    tabControl!: TabViewComponent;

    companyId: number;

    company = signal<CompanyView | undefined>(undefined);

    currentTab: string;

    listViewSelectedItem?: CompanyView;

    isActive?: boolean;

    entityTypeId: EntityTypes;

    tabsCounters: TabCounterType = {
        Buildings: 0,
        Contacts: 0,
        Opportunities: 0,
        Notes: 0
    };

    entitySubscription?: Subscription;

    private readonly companyService = inject(CompanyService);
    private readonly awaiter = inject(AwaiterService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly modalService = inject(ModalService);
    private readonly tabCounterService = inject(TabCounterService);

    constructor() {
        this.companyId = parseInt(this.route.snapshot.paramMap.get('id') ?? '0');
        this.currentTab = getTab(this.route.snapshot.paramMap.get('tab')) ?? TabNames[TabNames.Overview];
        this.isActive = true;
        this.entityTypeId = EntityTypes.Company;
    }

    async ngOnInit(): Promise<void> {
        await this.updateTabCounters();

        this.entitySubscription = this.route.data.subscribe(async ({ entityData }) => {
            this.company.set(entityData);
            this.isActive = this.company()?.isActive;
        });

        this.route.paramMap.subscribe(async (params) => {
            const newTab = getTab(params.get('tab'));

            if (newTab && newTab !== this.currentTab && this.tabControl) {
                this.currentTab = processTabParameter(this.route, this.tabControl);
            }
        });
    }

    ngOnDestroy(): void {
        if (this.entitySubscription) {
            this.entitySubscription?.unsubscribe();
        }
    }

    onEditCompany(): void {
        this.router.navigate([`/companies-edit/${this.companyId}`]);
    }

    async archiveCompany(): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Archive',
            cancelCaption: 'Cancel',
            content: 'Are you sure you want to archive this company?',
            title: 'Archive Company'
        });

        if (!responseOk) return;

        await this.awaiter.awaitAction('Archiving Company', async () => {
            const id = this.company()?.id;
            if (!id) return;

            await this.companyService.deactivate(id);
            this.isActive = false;
        });
    }

    async restoreCompany(): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Restore',
            cancelCaption: 'Cancel',
            content: 'Are you sure you want to restore this company?',
            title: 'Restore Company'
        });

        if (!responseOk) return;

        await this.awaiter.awaitAction('Restoring Company', async () => {
            const id = this.company()?.id;
            if (!id) return;

            await this.companyService.activate(id);
            this.isActive = true;
        });
    }

    async handleCompanyChange(): Promise<void> {
        if (!this.companyId) return;

        const res = await this.updateCompanyInfo();
        if (res) await this.updateTabCounters();
    }

    async onTabCountersChange(): Promise<void> {
        await this.updateTabCounters();
    }

    tabChange(tabName: string) {
        this.router.navigate(['/companies-single', this.companyId, tabName]);
    }

    private async updateCompanyInfo(): Promise<boolean | undefined> {
        return await this.awaiter.awaitAction('Getting Company Info', async () => {
            const company = await this.companyService.getById(this.companyId);

            this.company.set(company);
            this.isActive = company.isActive;

            return true;
        });
    }
    private async updateTabCounters(): Promise<void> {
        await this.tabCounterService.updateCounters(this.entityTypeId, this.companyId, this.tabsCounters);
    }
}
